import { addNotification, CLOSE_MODAL, SET_CARD_VIEW } from '@va/dashboard/actions/ui';
import { BusinessInfoForm, getBusinessInfoSchema } from '@va/dashboard/components';
import { getCardView, isModalOpen as modalOpen } from '@va/dashboard/selectors/ui';
import { useBusinessInfo } from '@va/dashboard/shared/account-settings';
import { CloseIconWithCircle, FeedbackSuccess } from '@va/icons';
import { useTranslate } from '@va/localization';
import { GET_AGENCY_CUSTOMER_REQUEST } from '@va/standalone/shared/actions';
import { createAgencyCustomer, updateAgencyCustomer } from '@va/standalone/shared/api-client/billing';
import { modalName } from '@va/standalone/shared/constants';
import { getUserId } from '@va/standalone/shared/helpers';
import { BusinessInfoType } from '@va/types/account';
import { Button, fontWeights, GrayGradientDivider, Heading5 } from '@va/ui/design-system';
import { ModalWrapper } from '@va/util/components';
import { renderIf } from '@va/util/helpers';
import { FormikConfig, useFormik } from 'formik';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const FORM_ID = 'businessInfoForm';

enum businessInfoModalView {
  initial = 'initial',
  messageView = 'messageView',
}

const BusinessInfo = () => {
  const translate = useTranslate();
  const dispatch = useDispatch();
  const isModalOpen = useSelector((state) => modalOpen(state, modalName.BUSINESS_INFO));
  const { businessInfo } = useBusinessInfo();

  const formInitialValues: BusinessInfoType = {
    firstName: businessInfo?.firstName || '',
    lastName: businessInfo?.lastName || '',
    companyName: businessInfo?.companyName || '',
    country: businessInfo?.country || '',
    vat: businessInfo?.vat || '',
    state: businessInfo?.state || '',
    city: businessInfo?.city || '',
    zipcode: businessInfo?.zipcode || '',
    street: businessInfo?.street || '',
    companyWebsite: businessInfo?.companyWebsite || '',
    businessSector: businessInfo?.businessSector || '',
    businessType: businessInfo?.businessType || '',
    businessSize: businessInfo?.businessSize || '',
    id: businessInfo?.id || '',
  };

  const modalView = useSelector((state) => getCardView(state, modalName.BUSINESS_INFO));

  const renderInitialView = renderIf(businessInfoModalView.initial === modalView);
  const renderMessageView = renderIf(businessInfoModalView.messageView === modalView);

  const handleModaViewSwitch = useCallback(
    function (view: businessInfoModalView, previousView?: businessInfoModalView) {
      dispatch({
        type: SET_CARD_VIEW,
        cardName: modalName.BUSINESS_INFO,
        view,
        previousView,
      });
    },
    [dispatch],
  );

  function handleCancelClick() {
    dispatch({ type: CLOSE_MODAL, modalName: modalName.BUSINESS_INFO });
    handleModaViewSwitch(businessInfoModalView.initial);
  }

  const handleFormSubmission = useCallback<FormikConfig<BusinessInfoType>['onSubmit']>(
    function (values, { setSubmitting }) {
      const userId = getUserId();
      const agencyId = businessInfo?.id;
      const agencyInfoCall = agencyId
        ? updateAgencyCustomer(userId, agencyId, values)
        : createAgencyCustomer(userId, values);
      agencyInfoCall
        .then(() => {
          setSubmitting(false);
          dispatch({ type: GET_AGENCY_CUSTOMER_REQUEST });
          handleModaViewSwitch(businessInfoModalView.messageView, businessInfoModalView.initial);
        })
        .catch(() => {
          setSubmitting(false);
          dispatch(
            addNotification({
              title: translate('notifications.generalError'),
              level: 'error',
              autoDismiss: 10,
            }),
          );
        });
    },
    [businessInfo, dispatch, handleModaViewSwitch, translate],
  );

  const formikForm = useFormik({
    initialValues: formInitialValues,
    onSubmit: handleFormSubmission,
    validationSchema: getBusinessInfoSchema(translate),
    enableReinitialize: true,
  });

  const { isValid, isSubmitting, dirty } = formikForm;

  return (
    <ModalWrapper closeModal={handleCancelClick} isModalOpen={isModalOpen}>
      <div className='max-w-[45rem] overflow-hidden'>
        <div className='flex justify-between items-center p-9px pl-6'>
          <Heading5 className='text-wrap' colorClassName='text-gray-charcoal' weight={fontWeights.semibold}>
            {businessInfoModalView.messageView === modalView
              ? translate('modal.confirmation.succeeded.title')
              : translate('modal.businessInfo.title')}
          </Heading5>
          <Button color='quinary' icon={() => <CloseIconWithCircle />} onClick={handleCancelClick} />
        </div>
        <GrayGradientDivider />
        {renderInitialView(<BusinessInfoForm id={FORM_ID} form={formikForm} />)}
        {renderMessageView(
          <div className='text-center w-[45rem]'>
            <FeedbackSuccess className='mx-auto mt-6 w-60px h-60px' />
            <p className='mt-4 mb-8'>{translate(`modal.${modalName.BUSINESS_INFO}.confirmation.succeeded`)}</p>
          </div>,
        )}
        <GrayGradientDivider />
        <div className='p-3'>
          {businessInfoModalView.initial === modalView && (
            <Button
              className='w-full'
              text={translate('button.save')}
              disabled={!isValid || !dirty || isSubmitting}
              type='submit'
              form={FORM_ID}
            />
          )}
          {businessInfoModalView.messageView === modalView && (
            <Button color='quinary' className='w-full' text={translate('button.close')} onClick={handleCancelClick} />
          )}
        </div>
      </div>
    </ModalWrapper>
  );
};

export default BusinessInfo;
